import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO } from './SEO';

interface FAQItem {
  question: string;
  answer: string;
}

interface ArticleSEOProps {
  title: string;
  description: string;
  slug: string;
  content: string;
  imageUrl?: string;
  author?: string;
  publishedAt?: string;
  modifiedAt?: string;
  category?: string;
  tags?: string[];
  readTime?: string;
  faqItems?: FAQItem[];
}

const SITE_URL = 'https://pravnibot.cz';
const ORGANIZATION_NAME = 'Pravnibot.cz';
const ORGANIZATION_LOGO = 'https://pravnibot.cz/logo512.png';

// Extract FAQ items from markdown content
const extractFAQFromContent = (content: string): FAQItem[] => {
  const faqItems: FAQItem[] = [];

  // Match FAQ section patterns like "### ? Question?" followed by answer
  const faqRegex = /###\s*[?]\s*(.+?)\?\s*\n+([\s\S]*?)(?=###|$)/gi;
  let match;

  while ((match = faqRegex.exec(content)) !== null) {
    const question = match[1].trim() + '?';
    const answer = match[2].trim()
      .replace(/\n+/g, ' ')
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .slice(0, 500);

    if (question && answer) {
      faqItems.push({ question, answer });
    }
  }

  return faqItems.slice(0, 10); // Max 10 FAQ items
};

export const ArticleSEO: React.FC<ArticleSEOProps> = ({
  title,
  description,
  slug,
  content,
  imageUrl,
  author = 'PravniBOT.cz',
  publishedAt,
  modifiedAt,
  category,
  tags = [],
  readTime,
  faqItems,
}) => {
  const canonicalUrl = `${SITE_URL}/blog/${slug}`;
  const articleImage = imageUrl || `${SITE_URL}/og-image.png`;

  // Extract FAQ from content if not provided
  const extractedFAQ = faqItems || extractFAQFromContent(content);

  // Article Schema.org markup
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    image: articleImage,
    author: {
      '@type': 'Organization',
      name: ORGANIZATION_NAME,
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: ORGANIZATION_LOGO,
      },
    },
    publisher: {
      '@type': 'Organization',
      name: ORGANIZATION_NAME,
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: ORGANIZATION_LOGO,
      },
    },
    datePublished: publishedAt,
    dateModified: modifiedAt || publishedAt,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    articleSection: category || 'Pravo',
    keywords: tags.join(', '),
    inLanguage: 'cs-CZ',
    isAccessibleForFree: true,
    ...(readTime && {
      timeRequired: `PT${readTime.replace(/\D/g, '')}M`,
    }),
  };

  // FAQ Schema.org markup
  const faqSchema = extractedFAQ.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: extractedFAQ.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  } : null;

  // Breadcrumb Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Domu',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${SITE_URL}/blog`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: title,
        item: canonicalUrl,
      },
    ],
  };

  // WebApplication Schema (AI information tool - NOT legal service)
  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: ORGANIZATION_NAME,
    url: SITE_URL,
    description: 'AI nastroj pro orientacni pravni informace - vyhledavani v legislative, analyza textu smluv, vzorove sablony dokumentu. Nejedna se o pravni sluzbu.',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'CZK',
    },
    provider: {
      '@type': 'Organization',
      name: ORGANIZATION_NAME,
    },
  };

  return (
    <>
      <SEO
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={articleImage}
        ogImageAlt={title}
        twitterCard="summary_large_image"
      >
        {/* Article-specific Open Graph */}
        {publishedAt && <meta property="article:published_time" content={publishedAt} />}
        {modifiedAt && <meta property="article:modified_time" content={modifiedAt} />}
        {author && <meta property="article:author" content={author} />}
        {category && <meta property="article:section" content={category} />}
        {tags.map((tag, index) => (
          <meta key={index} property="article:tag" content={tag} />
        ))}
      </SEO>

      <Helmet>
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>

        {/* FAQ Schema */}
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>

        {/* WebApplication Schema */}
        <script type="application/ld+json">
          {JSON.stringify(webAppSchema)}
        </script>
      </Helmet>
    </>
  );
};

export default ArticleSEO;
